import React, { useState } from 'react'
import { startCheckout } from '../api'
import Button from '../components/Button'
import { ErrorAlert } from '../components/Alert'
import { TIERS } from '../tiers'

/**
 * Plan picker that starts a Stripe Checkout session for the chosen tier and
 * redirects the browser to Stripe's hosted page. The subscription is activated
 * by the Stripe webhook on return; see BillingResult.jsx.
 *
 * @param {string} currentTier  - the client's active plan_tier (to mark "current")
 */
export default function UpgradePlans({ currentTier }) {
  const [loadingTier, setLoadingTier] = useState(null)
  const [error, setError] = useState('')

  async function handleUpgrade(tier) {
    if (loadingTier) return
    setError('')
    setLoadingTier(tier)
    try {
      const { checkout_url } = await startCheckout(tier)
      // Full-page redirect to Stripe's hosted checkout.
      window.location.assign(checkout_url)
    } catch (err) {
      setError(err.message || 'Could not start checkout. Please try again.')
      setLoadingTier(null)
    }
  }

  return (
    <div className="space-y-4">
      <ErrorAlert message={error} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIERS.map(tier => {
          const isCurrent = currentTier === tier.key
          return (
            <div
              key={tier.key}
              className={`rounded-xl border p-5 flex flex-col ${
                tier.highlight ? 'border-brand-300 ring-1 ring-brand-100' : 'border-gray-200'
              } bg-white`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-semibold text-gray-900">{tier.name}</h3>
                {tier.highlight && (
                  <span className="text-xs font-medium text-brand-500">Popular</span>
                )}
              </div>
              <p className="mt-1">
                <span className="text-2xl font-bold text-gray-900">{tier.price}</span>
                <span className="text-sm text-gray-500">/mo</span>
              </p>
              <p className="text-sm text-gray-500 mt-2 flex-1">{tier.blurb}</p>
              <div className="mt-4">
                {isCurrent ? (
                  <Button variant="secondary" size="sm" disabled className="w-full">
                    Current plan
                  </Button>
                ) : (
                  <Button
                    variant={tier.highlight ? 'primary' : 'secondary'}
                    size="sm"
                    className="w-full"
                    loading={loadingTier === tier.key}
                    disabled={!!loadingTier}
                    onClick={() => handleUpgrade(tier.key)}
                  >
                    Choose {tier.name}
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <p className="text-xs text-gray-500 text-center">
        Secure payment via Stripe. Cancel anytime.
      </p>
    </div>
  )
}
